import React, { Component, Suspense, lazy } from "react";

import { Layout } from "antd";

import { TEMPLATE_SIDES } from "../Common/Template/TemplateContext";
import PageBreadcrumb from "../Common/UIComponents/PageBreadcrumb";
import SideNav, { getSideNavCollapsed } from "../Common/UIComponents/SideNav";
import PaymentPendingBanner from "../grace-period/components/PaymentPendingBanner";
import Analytics from "./Analytics/Analytics";
import Calendar from "./Calendar/Calendar";
import EnvAnalyticsTab from "./EnvAnalyticsTab/EnvAnalyticsTab";
// Disabled: Swabathon requires @dnd-kit/core, which is not in the current package.json.
// import Swabathon from "./Swabathon/Swabathon";

import { hasPermission, permissions } from "../../utils/Permissions";

import { ReactComponent as EnvironmentCalendarActive } from "../../assets/icons/EnvironmentCalendarActive.svg";
import { ReactComponent as EnvironmentCalendarInactive } from "../../assets/icons/EnvironmentCalendarInactive.svg";
import { ReactComponent as AnalyticsIcon } from "../../assets/images/feature-icons/AnalyticsOutlined.svg";
import { ReactComponent as ReportIcon } from "../../assets/images/feature-icons/ReportOutlined.svg";
import { ReactComponent as TemplateIcon } from "../../assets/images/feature-icons/TemplateOutlined.svg";

import "./Env.css";

// Lazy-load Template so its CSS/deps do not affect the Env Report tab
const Template = lazy(() => import("../Common/Template/Template"));

const { Sider, Content } = Layout;

class Env extends Component {
  _isMounted = false;

  constructor(props) {
    super(props);

    this.state = {
      activeTab: "1",
      sideNavCollapsed: getSideNavCollapsed(),
    };

    this.handleSelect = this.handleSelect.bind(this);
    this.handleSideNavCollapsedChange = this.handleSideNavCollapsedChange.bind(this);
  }

  componentDidMount() {
    const { onChangeReport } = this.props;
    this._isMounted = true;
    if (this._isMounted) {
      const url = window.location.href;
      let activeTab = "1";
      let subUrl = "report";

      // Check account permission for calendar (controlled by accountPermissions only)
      const hasTemplateAccess = hasPermission(permissions.environment.template);
      const hasCalendarAccess = hasPermission(permissions.environment.calendar);
      const hasAnalyticsAccess = hasPermission(permissions.environment.analytics);
      // Disabled: Swabathon feature (missing package.json dep: @dnd-kit/core)
      // const hasSwabathonAccess = hasPermission(permissions.environment.swabathon);

      if (url.includes("/template")) {
        if (hasTemplateAccess) {
          activeTab = "2";
          subUrl = "template";
        } else {
          // Redirect to default if no permission
          activeTab = "1";
          subUrl = "";
        }
      } else if (url.includes("/calendar")) {
        if (hasCalendarAccess) {
          activeTab = "3";
          subUrl = "calendar";
        } else {
          // Redirect to default if no permission
          activeTab = "1";
          subUrl = "";
        }
      } else if (url.includes("/analytics")) {
        if (hasAnalyticsAccess) {
          activeTab = "4";
          subUrl = "analytics";
        } else {
          // Redirect to default if no permission
          activeTab = "1";
          subUrl = "";
        }
      } else if (url === "/environment" || (url.includes("/environment") && !url.includes("/calendar") && !url.includes("/analytics") && !url.includes("/template") && !url.includes("/swabathon"))) {
      // } else if (url.includes("/swabathon")) {
        // Disabled: Swabathon feature (missing package.json dep: @dnd-kit/core) — redirect to default
        // if (hasSwabathonAccess) {
        //   activeTab = "4";
        //   subUrl = "swabathon";
        // } else {
        //   // Redirect to default if no permission
        //   activeTab = "1";
        //   subUrl = "";
        // }
        activeTab = "1";
        subUrl = "";
      }
      if (this._isMounted) {
        this.setState({ activeTab });
      }
      onChangeReport(subUrl);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  handleSelect(selectedTab) {
    const { history } = this.props;
    if (this._isMounted) {
      // Check permissions before navigation (controlled by accountPermissions only)
      const hasTemplateAccess = hasPermission(permissions.environment.template);
      const hasCalendarAccess = hasPermission(permissions.environment.calendar);
      const hasAnalyticsAccess = hasPermission(permissions.environment.analytics);
      // Disabled: Swabathon feature (missing package.json dep: @dnd-kit/core)
      // const hasSwabathonAccess = hasPermission(permissions.environment.swabathon);

      this.setState({ activeTab: selectedTab }, () => {
        if (selectedTab === "1") {
          history.replace("/environment");
        } else if (selectedTab === "2" && hasTemplateAccess) {
          history.replace("/environment/template");
        } else if (selectedTab === "3" && hasCalendarAccess) {
          history.replace("/environment/calendar");
        } else if (selectedTab === "4" && hasAnalyticsAccess) {
          history.replace("/environment/analytics");
        // } else if (selectedTab === "4" && hasSwabathonAccess) {
        //   history.replace("/environment/swabathon");
        } else {
          // Fallback to default if no permission
          this.setState({ activeTab: "1" });
          history.replace("/environment");
        }
      });
    }
  }

  handleSideNavCollapsedChange(collapsed) {
    if (this._isMounted) {
      this.setState({ sideNavCollapsed: collapsed });
    }
  }

  render() {
    const { history } = this.props;
    const { activeTab, sideNavCollapsed } = this.state;

    // Check account permission for calendar (controlled by accountPermissions only)
    const hasCalendarAccess = hasPermission(permissions.environment.calendar);
    // Disabled: Swabathon feature (missing package.json dep: @dnd-kit/core)
    // const hasSwabathonAccess = hasPermission(permissions.environment.swabathon);

    // Build items array based on permissions
    const tabItems = [
      {
        key: "1",
        label: "Report",
        breadcrumbLabel: "Reports",
        icon: <ReportIcon />,
        content: <Analytics />,
      },
      hasPermission(permissions.environment.template) && {
        key: "2",
        label: "Template",
        breadcrumbLabel: "Template",
        icon: <TemplateIcon />,
        content: (
          <Suspense fallback={null}>
            <Template history={history} side={TEMPLATE_SIDES.ENVIRONMENTAL} />
          </Suspense>
        ),
      },
      hasCalendarAccess && {
        key: "3",
        label: "Calendar",
        breadcrumbLabel: "Calendar",
        icon: <EnvironmentCalendarInactive />,
        iconActive: <EnvironmentCalendarActive />,
        content: <Calendar />,
      },
      hasPermission(permissions.environment.analytics) && {
        key: "4",
        label: "Analytics",
        breadcrumbLabel: "Analytics",
        icon: <AnalyticsIcon />,
        content: <EnvAnalyticsTab />,
      },
      // Disabled: Swabathon feature (missing package.json dep: @dnd-kit/core)
      // hasSwabathonAccess && {
      //   key: "4",
      //   label: "Swabathon",
      //   icon: <ReportIcon className="env-tab-icon-white" />,
      //   children: <Swabathon />,
      // },
    ].filter(Boolean);

    const sideNavItems = tabItems.map(({
      key, label, icon, iconActive,
    }) => ({
      key,
      label,
      icon,
      iconActive,
    }));

    const activeItem = tabItems.find((item) => item.key === activeTab) || tabItems[0];

    return (
      <div id="env-main-container" className="envMainContainer">
        {this._isMounted ? (
          <>
            {activeItem && (
              <PageBreadcrumb
                items={[
                  { label: "Environment" },
                  { label: activeItem.breadcrumbLabel },
                ]}
                showBack={false}
              />
            )}
            <Layout className="EnvLayout">
              <Sider
                width={220}
                collapsedWidth={64}
                collapsed={sideNavCollapsed}
                theme="light"
                trigger={null}
                className="EnvLayout__Sider"
              >
                <SideNav
                  ariaLabel="Environment navigation"
                  items={sideNavItems}
                  activeKey={activeTab}
                  collapsed={sideNavCollapsed}
                  onCollapsedChange={this.handleSideNavCollapsedChange}
                  onItemClick={(item) => this.handleSelect(item.key)}
                />
              </Sider>
              <Content className="EnvLayout__Content">
                <PaymentPendingBanner />
                {activeItem && (
                  <div className="EnvLayout__Body">
                    {activeItem.content}
                  </div>
                )}
              </Content>
            </Layout>
          </>
        ) : null}
      </div>
    );
  }
}

export default Env;
